import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import useOnClickOutside from 'use-onclickoutside';
import { sortSizeOptions } from '../../../utils/sizes';
import styles from './SizeSelection.module.scss';
import messages from './messages';

const SizeSelection = ({ sizes, selectedKey = undefined, onSelect }) => {
  const ref = useRef();
  const { formatMessage } = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  // Smallest to largest, same order as the PDP selector. Out of stock sizes keep
  // their place in the list and are rendered disabled.
  const sortedSizes = sortSizeOptions(sizes, (size) => size.key);

  const selectedSize = sizes.find((size) => size.key === selectedKey);

  const handleSizeSelect = (sizeKey) => {
    onSelect(sizeKey);
    setIsExpanded(false);
  };

  const hasDiscountedPrice = (size) =>
    !!(
      size?.discountedPrice &&
      size?.price &&
      size.discountedPrice.centAmount < size.price.centAmount
    );

  useOnClickOutside(ref, () => {
    setIsExpanded(false);
  });

  return (
    <div
      ref={ref}
      className={classNames(styles.container, { [styles.opened]: isExpanded })}
    >
      <button
        className={styles.selected}
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className={styles['selected-label']}>
          <span>{selectedSize?.label}</span>
          {!isExpanded && (
            <>
              <span>-</span>
              {hasDiscountedPrice(selectedSize) && (
                <span className={styles['selected-price']}>
                  {selectedSize?.priceAsText}
                </span>
              )}
              <span className={styles['selected-discounted']}>
                {selectedSize?.discountedPriceAsText}
              </span>
            </>
          )}
        </div>
        <div className={styles.dropdownImg} />
      </button>

      <div className={styles.list}>
        {sortedSizes.map((size) => (
          <button
            key={size.key}
            type="button"
            className={classNames(styles['list-item'], {
              [styles['list-item-disabled']]: size.isOutOfStock,
              // The selected size stays bold while another one is hovered. Bold
              // only moves once the hovered size is actually selected.
              [styles['list-item-selected']]: size.key === selectedSize?.key,
            })}
            onClick={() => !size.isOutOfStock && handleSizeSelect(size.key)}
          >
            <span className={styles['list-item-label']}>{size.label}</span>
            {!size.isOutOfStock && hasDiscountedPrice(size) && (
              <span className={styles['list-item-price']}>
                {size.priceAsText}
              </span>
            )}
            {!size.isOutOfStock && (
              <span className={styles['list-item-discounted']}>
                {size.discountedPriceAsText}
              </span>
            )}
            {size.isOutOfStock && (
              <span className={styles['list-item-out-of-stock']}>
                {formatMessage(messages.outOfStock)}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

SizeSelection.propTypes = {
  sizes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      priceAsText: PropTypes.string,
      discountedPriceAsText: PropTypes.string,
      isOutOfStock: PropTypes.bool,
    })
  ).isRequired,
  selectedKey: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default SizeSelection;
